"use client";

import React from "react";
import { Mail, Phone, MapPin, Send, Building2, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-primary/20 flex items-center justify-center mb-4">
          <Send className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Message Sent</h3>
        <p className="text-muted-foreground max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">Company (Optional)</FormLabel>
            <FormControl>
              <Input placeholder="Your Company Ltd." className="bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80">How can we help?</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your automation needs..." className="min-h-[150px] resize-none bg-background/50 border-border/50 focus-visible:ring-primary focus-visible:border-primary transition-all duration-300" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-lg shadow-primary/20">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending Message...
          </> : <>
            <Send className="mr-2 size-5" />
            Send Message
          </>}
      </Button>
    </Form>;
}
export default function ContactGetInTouch() {
  return <section id="contactgetintouch" className="relative py-24 lg:py-32 bg-background overflow-hidden">
      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[120px] pointer-events-none opacity-50" />
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-primary/5 rounded-lg blur-[150px] pointer-events-none opacity-50" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
            Get In Touch
          </h2>
          <p className="text-lg text-muted-foreground">
            Ready to transform your business with intelligent automation? Reach out to our team of experts to discuss your specific needs and discover how DataZen can help.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
          
          {}
          <div className="lg:col-span-5 space-y-8">
            <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl overflow-hidden relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <CardContent className="p-8 lg:p-10 relative z-10">
                <div className="space-y-10">
                  <div>
                    <h3 className="text-2xl font-default font-semibold text-foreground mb-2">Contact Information</h3>
                    <p className="text-muted-foreground">
                      Fill out the form or contact us directly using the details below.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="mt-1 p-3 rounded-lg bg-primary/10 text-primary">
                        <Mail className="size-6" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Email</p>
                        <Link to="mailto:office@datazenlab.com" className="text-lg font-medium text-foreground hover:text-primary transition-colors">
                          office@datazenlab.com
                        </Link>
                      </div>
                    </div>

                    

                    <div className="flex items-start space-x-4">
                      <div className="mt-1 p-3 rounded-lg bg-primary/10 text-primary">
                        <Building2 className="size-6" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">Headquarters</p>
                        <p className="text-lg font-medium text-foreground">Gothenburg, Sweden</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-border/50">
                    <Button asChild variant="outline" className="w-full h-12 border-primary/30 hover:bg-primary/10 hover:text-primary transition-colors">
                      <Link to="mailto:office@datazenlab.com">
                        <Mail className="mr-2 size-4" />
                        Email Us Directly
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="bg-card/40 backdrop-blur-xl border-border/50 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />
              <CardContent className="p-8 lg:p-10">
                <FormProvider formId="contact-form-get-in-touch" sectionName="ContactGetInTouch" defaultValues={{
                name: "",
                email: "",
                company: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                message: {
                  required: true,
                  minLength: 10
                }
              }} submitText="Send Message" successMessage="Thank you for reaching out! Our team will get back to you shortly.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}